import React from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Trans } from 'react-i18next';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import RoomBookingUserAvatar from '../RoomBookingUserAvatar';
import ChatWithUserIconButton from '../chat/ChatWithUserIconButton';
import UserBandAffiliationLine from '../UserBandAffiliationLine';
import LayeredTutorBookingAvatars from './LayeredTutorBookingAvatars';
import TutorBookingChangeProposalScheduleChip from './TutorBookingChangeProposalScheduleChip';
import TutorBookingScheduleBlock from './TutorBookingScheduleBlock';
import { HostTutorBookingResponse } from '../../models/Tutor';
import {
  formatTutorBookingScheduleLabels,
  getProposalNewEndIso,
  getProposalOldEndIso,
  getTutorBookingPreviousTimeLabel,
  proposalTimeChanged,
} from '../../utils/tutorBookingChangeProposal';
import {
  getTutorBookingStatusChipColor,
  getTutorBookingStatusChipVariant,
  tutorBookingStatusLabel,
} from '../../utils/tutorBookingStatus';
import {
  hasPendingTutorChangeProposal,
  TUTOR_BOOKING_REQUEST_ACTION_BUTTON_SX,
  TutorBookingRequestActionsCell,
} from '../TutorBookingRequestTableCells';
import TutorLessonSessionTypeChip from './TutorLessonSessionTypeChip';
import TutorBookingPaymentStatusChips from './TutorBookingPaymentStatusChips';
import TutorLessonAttendanceChip from './TutorLessonAttendanceChip';
import PassBookingChip from '../passes/PassBookingChip';
import BookingCreatedAtLabel from '../BookingCreatedAtLabel';
import HostTutorBookingRoomAssignmentField from '../host/HostTutorBookingRoomAssignmentField';
import { Room } from '../../models/Room';
import {
  canHostAssignTutorBookingRoom,
  canShowTutorBookingRoomSection,
  normalizeTutorBookingStatus,
} from '../../utils/tutorBookingStatus';
import { tutorBookingDetailsPath } from '../../utils/tutorBookingDetailsPath';
import { tutorBookingStudentNames } from '../../utils/tutorBookingStudentName';
import {
  bookingCalendarDateKey,
  hostSpaceCalendarPath,
} from '../../utils/hostSpaceCalendarPath';

export type TutorBookingRequestCardPerspective = 'host' | 'tutor';

export interface TutorBookingRequestCardProps {
  row: HostTutorBookingResponse;
  perspective: TutorBookingRequestCardPerspective;
  busy: boolean;
  processingBookingId: string | null;
  showModeration: boolean;
  canPropose: boolean;
  canCancelProposal: boolean;
  canCancelLesson?: boolean;
  acceptDisabled: boolean;
  declineDisabled: boolean;
  onAccept: (bookingId: string) => void;
  onDecline: (bookingId: string) => void;
  onPropose: (row: HostTutorBookingResponse) => void;
  onCancelProposal: (row: HostTutorBookingResponse) => void;
  onCancelLesson?: (row: HostTutorBookingResponse) => void;
  canRefund?: boolean;
  onRefund?: (row: HostTutorBookingResponse) => void;
  refundLoading?: boolean;
  canCollectPayment?: boolean;
  onCollectPayment?: (row: HostTutorBookingResponse) => void;
  collectPaymentLoading?: boolean;
  /** Host-only: rooms for this booking's space (for offline room assignment). */
  spaceRooms?: Room[];
  spaceRoomsLoading?: boolean;
  onAssignRoom?: (bookingId: string, roomId: string) => void;
  onClearRoom?: (bookingId: string) => void;
  conflictFooter?: React.ReactNode;
  /** Highlight from push / deep link (`?bookingId=`). */
  highlighted?: boolean;
}

const TutorBookingRequestCard: React.FC<TutorBookingRequestCardProps> = ({
  row,
  perspective,
  busy,
  processingBookingId,
  showModeration,
  canPropose,
  canCancelProposal,
  canCancelLesson = false,
  acceptDisabled,
  declineDisabled,
  onAccept,
  onDecline,
  onPropose,
  onCancelProposal,
  onCancelLesson,
  canRefund = false,
  onRefund,
  refundLoading = false,
  canCollectPayment = false,
  onCollectPayment,
  collectPaymentLoading = false,
  spaceRooms = [],
  spaceRoomsLoading = false,
  onAssignRoom,
  onClearRoom,
  conflictFooter,
  highlighted = false,
}) => {
  const { t, i18n, bookingCopyLocale } = useTranslation();
  const navigate = useNavigate();
  const pendingProposal = hasPendingTutorChangeProposal(row.changeProposal);
  const needsModeration = showModeration;
  const showRoomSection =
    perspective === 'host' &&
    canShowTutorBookingRoomSection({
      spaceId: row.spaceId,
      lessonSessionType: row.lessonSessionType,
    });
  const canAssignRoom =
    showRoomSection &&
    !!onAssignRoom &&
    !!onClearRoom &&
    canHostAssignTutorBookingRoom({
      status: row.status,
      startTime: row.startTime,
      spaceId: row.spaceId,
      lessonSessionType: row.lessonSessionType,
    });
  const roomAssignmentHandlers =
    onAssignRoom && onClearRoom
      ? { onAssign: onAssignRoom, onClear: onClearRoom }
      : null;

  const formatMoney = (amount: number, currency: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currency || 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) {
      return `${remainingMinutes} ${t('roomBookingRequests.min')}`;
    }
    if (remainingMinutes === 0) {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')}`;
    }
    return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')} ${remainingMinutes} ${t('roomBookingRequests.min')}`;
  };

  const formatBookingSchedule = (
    startTime: string,
    endTime: string,
    options?: { timezone?: string | null; source?: 'display' | 'proposal' },
  ) => formatTutorBookingScheduleLabels(startTime, endTime, i18n.language, formatDuration, options);

  const studentNames = tutorBookingStudentNames({
    studentProfile: row.studentProfile,
    userName: row.userName,
  });
  const studentName =
    studentNames.primaryName ||
    (perspective === 'host'
      ? t('hostTutorBookings.user', 'Student')
      : t('tutorBookings.user', 'Student'));

  const tutorName =
    row.tutorName?.trim() || t('hostTutorBookings.tutor', 'Tutor');

  const primaryName = studentName;

  const avatarImageUrl = row.userProfileImageUrl;

  const lessonOptionTitle =
    row.lessonOptionName?.trim() ||
    t('userTutorBookings.tutorSession', 'Tutor session');

  const hasActions = showModeration || canPropose || canCancelLesson || canRefund;
  const showActionsSection = hasActions || Boolean(conflictFooter);
  const showCollectPaymentCta = Boolean(canCollectPayment && onCollectPayment);
  const collectPaymentLabel = t('hostTutorBookings.collectPayment', 'Mark as paid');
  const collectPaymentDisabled = busy || collectPaymentLoading;

  const openBookingDetails = () => {
    if (busy) return;
    navigate(tutorBookingDetailsPath(perspective, row.bookingId));
  };

  const bookingStatusChip = (
    <Chip
      color={getTutorBookingStatusChipColor(row.status)}
      variant={getTutorBookingStatusChipVariant(row.status)}
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            {tutorBookingStatusLabel(t, row.status)}
          </Box>
          {normalizeTutorBookingStatus(row.status) === 'CONFIRMED' ? (
            <TutorLessonAttendanceChip
              status={row.status}
              userConfirmedAt={row.userConfirmedAt}
              confirmAttendanceEnabled={row.confirmAttendanceEnabled}
              tone="inherit"
            />
          ) : null}
        </Box>
      }
      sx={{
        flexShrink: 0,
        height: 48,
        borderRadius: 1.5,
        fontWeight: 700,
        fontSize: '0.875rem',
        letterSpacing: '0.01em',
        '& .MuiChip-label': {
          px: 1.5,
          whiteSpace: 'normal',
          lineHeight: 1.25,
          textAlign: 'center',
        },
      }}
    />
  );

  const changeProposalStatusChip = row.changeProposal ? (
    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
      <TutorBookingChangeProposalScheduleChip
        proposal={row.changeProposal}
        bookingStartTime={row.startTime}
        bookingEndTime={row.endTime}
        locale={i18n.language}
        audience="host"
        timezone={row.timezone}
        showPreviousTimeInTooltip={
          !(pendingProposal && proposalTimeChanged(row.changeProposal))
        }
      />
      {canCancelProposal ? (
        <Tooltip
          title={t(
            'hostTutorBookings.changeProposal.withdrawTooltip',
            'Cancel schedule change',
          )}
          arrow
        >
          <span>
            <IconButton
              size="small"
              color="inherit"
              disabled={busy}
              aria-label={t(
                'hostTutorBookings.changeProposal.withdrawTooltip',
                'Cancel schedule change',
              )}
              onClick={() => onCancelProposal(row)}
              sx={{
                p: 0.35,
                color: 'text.secondary',
                '&:hover': { color: 'warning.main', bgcolor: 'action.hover' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
    </Stack>
  ) : null;

  const viewInCalendarLabel = t('hostTutorBookings.viewInCalendar', 'View in Calendar');
  const viewInCalendarTo =
    perspective === 'host' && row.spaceId?.trim()
      ? hostSpaceCalendarPath(row.spaceId.trim(), {
          date: bookingCalendarDateKey(
            pendingProposal && row.changeProposal?.newStartTime
              ? row.changeProposal.newStartTime
              : row.startTime,
            row.timezone,
          ),
        })
      : null;

  const renderScheduleSection = () => {
    const proposal = row.changeProposal;
    if (pendingProposal && proposal) {
      const proposalScheduleOptions = { source: 'proposal' as const, timezone: row.timezone };
      const proposedEndIso = getProposalNewEndIso(proposal, row.startTime, row.endTime);
      const proposedSchedule = formatBookingSchedule(
        proposal.newStartTime,
        proposedEndIso,
        proposalScheduleOptions,
      );

      if (proposalTimeChanged(proposal)) {
        const oldEndIso = getProposalOldEndIso(proposal, row.startTime, row.endTime);
        const previousSchedule = formatBookingSchedule(
          proposal.oldStartTime,
          oldEndIso,
          proposalScheduleOptions,
        );
        return (
          <TutorBookingScheduleBlock
            schedule={proposedSchedule}
            emphasize
            previousTimeLabel={getTutorBookingPreviousTimeLabel(previousSchedule, proposedSchedule)}
            statusChip={changeProposalStatusChip}
            viewInCalendarTo={viewInCalendarTo}
            viewInCalendarLabel={viewInCalendarLabel}
          />
        );
      }

      return (
        <TutorBookingScheduleBlock
          schedule={proposedSchedule}
          emphasize
          statusChip={changeProposalStatusChip}
          viewInCalendarTo={viewInCalendarTo}
          viewInCalendarLabel={viewInCalendarLabel}
        />
      );
    }

    return (
      <TutorBookingScheduleBlock
        schedule={formatBookingSchedule(row.startTime, row.endTime)}
        statusChip={changeProposalStatusChip}
        viewInCalendarTo={viewInCalendarTo}
        viewInCalendarLabel={viewInCalendarLabel}
      />
    );
  };

  return (
    <Card
      variant="outlined"
      onClick={openBookingDetails}
      role="link"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openBookingDetails();
        }
      }}
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2.5,
        overflow: 'hidden',
        cursor: busy ? 'default' : 'pointer',
        bgcolor: theme.palette.background.paper,
        borderColor: highlighted
          ? theme.palette.primary.main
          : needsModeration
            ? theme.palette.success.main
            : pendingProposal
              ? theme.palette.warning.main
              : theme.palette.divider,
        boxShadow: highlighted
          ? `0 0 0 1px ${theme.palette.primary.main}, 0 8px 24px ${alpha(theme.palette.primary.main, 0.18)}`
          : undefined,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: highlighted
            ? theme.palette.primary.main
            : needsModeration
              ? theme.palette.success.light
              : pendingProposal
                ? theme.palette.warning.light
                : alpha(theme.palette.primary.main, 0.5),
          boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.08)}`,
        },
      })}
    >
      <CardContent sx={{ flex: 1, px: { xs: 2, sm: 2.5 }, pt: 2.5, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            {perspective === 'host' ? (
              <LayeredTutorBookingAvatars
                primaryImageUrl={row.userProfileImageUrl}
                primaryName={studentName}
                primaryTooltip={t('hostTutorBookings.user', 'Student')}
                secondaryImageUrl={row.tutorProfileImageUrl}
                secondaryName={tutorName}
                secondaryTooltip={t('hostTutorBookings.tutor', 'Tutor')}
                size={48}
              />
            ) : (
              <RoomBookingUserAvatar
                userProfileImageUrl={avatarImageUrl}
                displayName={primaryName}
                size={48}
              />
            )}
            <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant="subtitle1"
                  component="h3"
                  fontWeight={700}
                  noWrap
                  sx={{ lineHeight: 1.3, minWidth: 0 }}
                >
                  {primaryName}
                </Typography>
                {row.userId?.trim() ? (
                  <ChatWithUserIconButton
                    partnerUserId={row.userId}
                    partnerName={primaryName}
                    basePath={perspective === 'host' ? '/host/chats' : '/tutor/chats'}
                    disabled={busy}
                    sx={{ flexShrink: 0 }}
                  />
                ) : null}
              </Stack>

              {studentNames.accountAlias ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ lineHeight: 1.3 }}
                >
                  {studentNames.accountAlias}
                </Typography>
              ) : null}

              {row.userPhoneNumber ? (
                <Stack direction="row" spacing={0.35} alignItems="center" sx={{ mt: 0.35 }}>
                  <PhoneOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="a"
                    href={`tel:${row.userPhoneNumber}`}
                    onClick={(event) => event.stopPropagation()}
                    sx={{ textDecoration: 'none' }}
                  >
                    {row.userPhoneNumber}
                  </Typography>
                </Stack>
              ) : null}

              {row.bandName?.trim() ? (
                <UserBandAffiliationLine bandName={row.bandName} />
              ) : null}

              {perspective === 'host' ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}
                  noWrap
                >
                  <Trans
                    lng={bookingCopyLocale ?? undefined}
                    i18nKey={
                      row.spaceName?.trim()
                        ? 'hostTutorBookings.lessonWithAt'
                        : 'hostTutorBookings.lessonWith'
                    }
                    defaults={
                      row.spaceName?.trim()
                        ? 'Lesson with <tutorLink>{{tutor}}</tutorLink> at <spaceLink>{{space}}</spaceLink>'
                        : 'Lesson with <tutorLink>{{name}}</tutorLink>'
                    }
                    values={
                      row.spaceName?.trim()
                        ? { tutor: tutorName, space: row.spaceName.trim() }
                        : { name: tutorName }
                    }
                    components={{
                      tutorLink: (
                        <MuiLink
                          component={RouterLink}
                          to={`/host/tutors/${row.tutorId}`}
                          variant="body2"
                          underline="hover"
                          onClick={(event) => event.stopPropagation()}
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            verticalAlign: 'baseline',
                          }}
                        />
                      ),
                      spaceLink:
                        row.spaceId && row.spaceName?.trim() ? (
                          <MuiLink
                            component={RouterLink}
                            to={`/host/spaces/${row.spaceId}`}
                            variant="body2"
                            underline="hover"
                            onClick={(event) => event.stopPropagation()}
                            sx={{
                              fontWeight: 600,
                              color: 'primary.main',
                              verticalAlign: 'baseline',
                            }}
                          />
                        ) : (
                          <Box component="span" sx={{ fontWeight: 600 }} />
                        ),
                    }}
                  />
                </Typography>
              ) : row.spaceName?.trim() ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, lineHeight: 1.35 }}
                  noWrap
                >
                  {t('hostTutorBookings.atSpace', 'at {{name}}', {
                    name: row.spaceName.trim(),
                  })}
                </Typography>
              ) : null}
            </Box>
            {bookingStatusChip}
          </Stack>

          <Stack spacing={0.75}>
            {renderScheduleSection()}
          </Stack>

          {showRoomSection && roomAssignmentHandlers ? (
            <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
              <HostTutorBookingRoomAssignmentField
                bookingId={row.bookingId}
                roomId={row.roomId}
                roomName={row.roomName}
                rooms={spaceRooms}
                roomsLoading={spaceRoomsLoading}
                canAssign={canAssignRoom}
                busy={busy}
                onAssign={roomAssignmentHandlers.onAssign}
                onClear={roomAssignmentHandlers.onClear}
              />
            </Box>
          ) : null}

          <Box
            sx={(theme) => ({
              position: 'relative',
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              ...(showCollectPaymentCta
                ? {
                    '&:hover .payment-collect-cta, &:focus-within .payment-collect-cta': {
                      opacity: 1,
                      pointerEvents: 'auto',
                    },
                    '@media (hover: none)': {
                      '& .payment-collect-cta': {
                        opacity: 1,
                        pointerEvents: 'auto',
                      },
                    },
                    '&:hover': {
                      borderColor: alpha(theme.palette.success.main, 0.55),
                    },
                  }
                : null),
            })}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <MusicNoteRoundedIcon
                sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
              />
              <Stack spacing={0.75} minWidth={0} sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {lessonOptionTitle}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TutorLessonSessionTypeChip sessionType={row.lessonSessionType} />
                  <TutorBookingPaymentStatusChips
                    paymentStatus={row.pass ? undefined : row.paymentStatus}
                    stripeRefundStatus={row.stripeRefundStatus ?? undefined}
                    refundReason={row.pass ? undefined : row.refundReason}
                    showRefundReason={!row.pass}
                    compact
                  />
                  {row.pass && <PassBookingChip pass={row.pass} />}
                </Stack>
              </Stack>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flexShrink: 0, alignSelf: 'center' }}
              >
                <PaymentsRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: row.pass ? 'text.secondary' : 'text.primary',
                  }}
                />
                <Typography
                  variant="body2"
                  color={row.pass ? 'text.secondary' : 'text.primary'}
                  sx={{
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    textDecoration: row.pass ? 'line-through' : 'none',
                  }}
                >
                  {formatMoney(row.totalAmount, row.currencyCode)}
                </Typography>
              </Stack>
            </Stack>
            {showCollectPaymentCta ? (
              <Box
                className="payment-collect-cta"
                component="button"
                type="button"
                aria-label={collectPaymentLabel}
                disabled={collectPaymentDisabled}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  onCollectPayment?.(row);
                }}
                onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
                sx={(theme) => ({
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  border: 'none',
                  margin: 0,
                  font: 'inherit',
                  color: 'text.secondary',
                  bgcolor: alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === 'dark' ? 0.82 : 0.9,
                  ),
                  opacity: 0,
                  pointerEvents: 'none',
                  transition: 'opacity 0.15s ease, color 0.15s ease',
                  px: 1.5,
                  cursor: collectPaymentDisabled ? 'not-allowed' : 'pointer',
                  '&:hover:not(:disabled)': {
                    color: 'success.main',
                  },
                })}
              >
                {collectPaymentLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PaymentsRoundedIcon sx={{ fontSize: 16 }} />
                )}
                <Typography
                  variant="button"
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {collectPaymentLabel}
                </Typography>
              </Box>
            ) : null}
          </Box>

          {row.reason?.trim() ? (
            <Box
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 1.25,
                bgcolor: 'action.hover',
                borderLeft: '2px solid',
                borderColor: 'info.main',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {row.reason.trim()}
              </Typography>
            </Box>
          ) : null}

          <BookingCreatedAtLabel
            createdAt={row.createdAt}
            timezone={row.timezone}
            placement="footer"
          />
        </Stack>
      </CardContent>

      {showActionsSection ? (
        <>
          <Divider />
          <CardActions
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1.5,
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 0.75,
            }}
          >
            {hasActions ? (
              <Box sx={{ width: '100%' }}>
                <TutorBookingRequestActionsCell
                  row={row}
                  busy={busy}
                  processingBookingId={processingBookingId}
                  showModeration={showModeration}
                  canPropose={canPropose}
                  canCancelProposal={false}
                  canCancelLesson={canCancelLesson}
                  acceptDisabled={acceptDisabled}
                  declineDisabled={declineDisabled}
                  onAccept={onAccept}
                  onDecline={onDecline}
                  onPropose={onPropose}
                  onCancelProposal={onCancelProposal}
                  onCancelLesson={onCancelLesson}
                  layout="card"
                />
              </Box>
            ) : null}
            {canRefund && onRefund ? (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => onRefund(row)}
                disabled={busy || refundLoading}
                startIcon={
                  refundLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  ...TUTOR_BOOKING_REQUEST_ACTION_BUTTON_SX,
                  alignSelf: 'flex-start',
                  '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
                }}
              >
                {t('hostTutorBookings.issueRefund', 'Issue refund')}
              </Button>
            ) : null}
            {conflictFooter}
          </CardActions>
        </>
      ) : null}
    </Card>
  );
};

export default TutorBookingRequestCard;
